import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import "../../_css/AIFeedback.css";
import Header from "../common/Header";
import { formatDateLong } from "../helper/formatters";
import { getAllPdfs } from "../../requests";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { deleteAuthToken } from "../../utils/helper";
import { notifyError } from "../helper/Toaster";
import Feedback from "../UI/Feedback";

const AIStudents = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const grade = searchParams.get("grade") ?? "";
  const exam = searchParams.get("exam") ?? "";

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showFeedBack, setShowFeedBack] = useState(false);
  const [viewFeedBack, setViewFeedBack] = useState(false);
  const [feedbackId, setFeedbackId] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [searchName, setSearchName] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setSearchName(nameInput.trim()), 1500);
    return () => clearTimeout(id);
  }, [nameInput]);

  const handleFeedbackClick = (rowId, type) => {
    setFeedbackId(rowId);
    if (type === 1) {
      setViewFeedBack(false);
      setShowFeedBack(true);
    } else {
      setShowFeedBack(false);
      setViewFeedBack(true);
    }
  };

  useEffect(() => {
    setNameInput("");
    setSearchName("");
  }, [grade, exam]);

  useEffect(() => {
    setPage(1);
  }, [grade, exam, searchName]);

  useEffect(() => {
    if (!grade || !exam) {
      setRows([]);
      setTotalCount(0);
      return;
    }

    const fetchStudents = async () => {
      dispatch(startLoader());
      try {
        const res = await getAllPdfs({
          page,
          limit,
          grade,
          exam_name: exam,
          ...(searchName ? { name: searchName } : {}),
        });
        const payload = res?.data;
        setRows(Array.isArray(payload?.data) ? payload.data : []);
        setTotalCount(Number(payload?.count) || 0);
      } catch (error) {
        console.log(error);
        setRows([]);
        setTotalCount(0);
        if (error?.response?.status === 403) {
          notifyError(
            error?.response?.data?.error ||
              "Your session has expired. Please log in again."
          );
          deleteAuthToken();
          navigate("/login");
          return;
        }
        notifyError(
          error?.response?.data?.error || "Error loading students for this exam."
        );
      } finally {
        dispatch(stopLoader());
      }
    };

    fetchStudents();
  }, [grade, exam, page, limit, searchName, dispatch, navigate]);

  const scoreLabel = (row) => {
    if (row.marks_obt != null && row.marks_total != null) {
      return `${row.marks_obt}/${row.marks_total}`;
    }
    return "—";
  };

  const rollLabel = (row) => {
    const r = row.student_rollno;
    if (r == null || r === "") return "—";
    return String(r);
  };

  return (
    <div className="page-main">
      <Header name={["AI Feedback", "Students"]} />
      <div className="ai-fd-main">
        <div className="ai-fd-con ai-fd-con--align-stretch">
          <div
            className="ai-fd-head"
            style={{
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div className="ai-fb-page-context" style={{ flex: "1 1 auto", width: "auto", minWidth: 0 }}>
              {grade || exam ? (
                <>
                  Grade{" "}
                  <span className="ai-fb-context-strong">{grade || "—"}</span>
                  <span className="ai-fb-context-sep"> · </span>
                  Exam{" "}
                  <span className="ai-fb-context-strong">{exam || "—"}</span>
                </>
              ) : (
                <span className="ai-fb-context-muted">
                  Select an exam from AI Feedback (View students).
                </span>
              )}
            </div>
            {grade && exam ? (
              <div className="ai-fd-right">
                <input
                  className="student-list-search"
                  style={{ height: "36px" }}
                  placeholder="Search name"
                  name="name"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              </div>
            ) : null}
          </div>

          <div className="ai-fb-table ai-fb-table-simple">
            <div className="ai-fb-table-head">
              <div className="ai-fb-head-cell" style={{ width: "6%" }}>
                S.No
              </div>
              <div className="ai-fb-head-cell" style={{ width: "18%" }}>
                Student name
              </div>
              <div className="ai-fb-head-cell" style={{ width: "10%" }}>
                Roll no
              </div>
              <div className="ai-fb-head-cell" style={{ width: "12%" }}>
                Score
              </div>
              <div className="ai-fb-head-cell" style={{ width: "16%" }}>
                Subject
              </div>
              <div className="ai-fb-head-cell" style={{ width: "16%" }}>
                Uploaded on
              </div>
              <div
                className="ai-fb-head-cell ai-fb-cell-center"
                style={{ width: "22%" }}
              >
                Action
              </div>
            </div>

            {!grade || !exam ? (
              <div className="ai-fb-empty">
                Open this page from AI Feedback with a grade and exam selected.
              </div>
            ) : rows.length === 0 ? (
              <div className="ai-fb-empty">
                No students for this grade and exam.
              </div>
            ) : null}

            {rows.map((row, index) => (
              <div key={row.id} className="ai-fb-group">
                <div className="ai-fb-main-row">
                  <div className="ai-fb-row-cell" style={{ width: "6%" }}>
                    {(page - 1) * limit + index + 1}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "18%" }}>
                    {row.student_name ?? "—"}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "10%" }}>
                    {rollLabel(row)}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "12%" }}>
                    {scoreLabel(row)}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "16%" }}>
                    {row.subject ?? "—"}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "16%" }}>
                    {row.created_at
                      ? formatDateLong(row.created_at)
                      : "—"}
                  </div>
                  <div
                    className="ai-fb-row-cell ai-fb-cell-center"
                    style={{
                      width: "22%",
                      display: "flex",
                      justifyContent: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      style={{ backgroundColor: "#E0F5FE", color: "#01A7DE" }}
                      onClick={() => handleFeedbackClick(row.id, 1)}
                      className="ai-fd-fb-btn"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="ai-fd-fb-btn"
                      onClick={() => handleFeedbackClick(row.id, 2)}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {grade && exam ? (
            <div
              className="di-flex"
              style={{
                width: "100%",
                marginTop: 16,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ color: "#6B7280" }}>
                Showing {(page - 1) * limit + 1} to{" "}
                {rows.length + (page - 1) * limit} out of {totalCount} items
              </div>
              <div
                className="di-flex"
                style={{ justifyContent: "end", gap: "10px" }}
              >
                <button
                  disabled={page === 1}
                  className="my-btn"
                  style={{
                    backgroundColor: "#E0F5FE",
                    color: "#01A7DE",
                    opacity: page === 1 ? 0.4 : 1,
                  }}
                  onClick={() => setPage((p) => (p > 1 ? p - 1 : p))}
                >
                  Previous
                </button>
                <button
                  disabled={rows.length + (page - 1) * limit >= totalCount}
                  className="my-btn"
                  style={{
                    backgroundColor: "#E0F5FE",
                    color: "#01A7DE",
                    opacity:
                      rows.length + (page - 1) * limit >= totalCount
                        ? 0.4
                        : 1,
                  }}
                  onClick={() => setPage((p) => (rows.length ? p + 1 : p))}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {showFeedBack && (
          <Feedback
            fn={() => setShowFeedBack(false)}
            name="Student"
            disabled={false}
            id={feedbackId}
          />
        )}
        {viewFeedBack && (
          <Feedback
            fn={() => setViewFeedBack(false)}
            name="Student"
            disabled={true}
            save={false}
            id={feedbackId}
          />
        )}
      </div>
    </div>
  );
};

export default AIStudents;
